/**
 * IBAN-controle.
 *
 * De organisator en de kraam typen hun eigen rekeningnummer in, en dat nummer
 * bepaalt waar het geld heen gaat. Een typefout komt er pas uit als er niets
 * aankomt — dan is het evenement al voorbij. De elfproef die in een IBAN zit
 * (mod 97) vangt vrijwel elke verschrijving en elke omgewisselde cijfercombinatie.
 *
 * Dit zegt niets over of de rekening bestaat of van de juiste persoon is; het
 * zegt alleen dat het nummer op zichzelf klopt.
 */

/** Lengte per land, voor de landen die we tegenkomen. */
const LENGTES: Record<string, number> = {
  NL: 18,
  BE: 16,
  DE: 22,
  FR: 27,
  ES: 24,
  IT: 27,
  LU: 20,
  GB: 22,
  PL: 28,
  PT: 25,
};

export function normaliseerIban(invoer: string): string {
  return invoer.replace(/[\s.-]/g, '').toUpperCase();
}

/** Toont een IBAN in groepjes van vier, zoals op een bankafschrift. */
export function toonIban(invoer: string): string {
  return (normaliseerIban(invoer).match(/.{1,4}/g) ?? []).join(' ');
}

function mod97(iban: string): number {
  // Eerste vier tekens naar achteren, letters naar getallen (A = 10 ... Z = 35).
  const herschikt = iban.slice(4) + iban.slice(0, 4);
  const cijfers = [...herschikt]
    .map((teken) => (/[A-Z]/.test(teken) ? String(teken.charCodeAt(0) - 55) : teken))
    .join('');

  // In stukjes rekenen, want het getal is te groot voor een gewone number.
  let rest = 0;
  for (const cijfer of cijfers) {
    rest = (rest * 10 + Number(cijfer)) % 97;
  }
  return rest;
}

export interface IbanControle {
  geldig: boolean;
  /** In gewone taal, klaar om onder het invoerveld te zetten. */
  melding: string | null;
  genormaliseerd: string;
}

export function controleerIban(invoer: string): IbanControle {
  const iban = normaliseerIban(invoer);

  if (iban === '') {
    return { geldig: false, melding: 'Vul een rekeningnummer in.', genormaliseerd: iban };
  }
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(iban)) {
    return {
      geldig: false,
      melding: 'Dit ziet er niet uit als een rekeningnummer. Een IBAN begint met twee letters.',
      genormaliseerd: iban,
    };
  }

  const land = iban.slice(0, 2);
  const verwachteLengte = LENGTES[land];
  if (verwachteLengte !== undefined && iban.length !== verwachteLengte) {
    return {
      geldig: false,
      melding:
        iban.length < verwachteLengte
          ? 'Dit rekeningnummer is te kort. Tel even na.'
          : 'Dit rekeningnummer is te lang. Tel even na.',
      genormaliseerd: iban,
    };
  }
  if (verwachteLengte === undefined && (iban.length < 15 || iban.length > 34)) {
    return {
      geldig: false,
      melding: 'Dit rekeningnummer heeft een onmogelijke lengte.',
      genormaliseerd: iban,
    };
  }

  if (mod97(iban) !== 1) {
    return {
      geldig: false,
      melding: 'Er zit een typefout in dit rekeningnummer. Controleer het bij je bank.',
      genormaliseerd: iban,
    };
  }

  return { geldig: true, melding: null, genormaliseerd: iban };
}

export function isGeldigeIban(invoer: string): boolean {
  return controleerIban(invoer).geldig;
}
